import { NgModule } from '@angular/core';
import type { Routes } from '@angular/router';
import { RouterModule } from '@angular/router';
import { NotFoundGuard } from '@freelancer/404';
import { AppEmptyComponent } from 'app/app-empty.component';
import { RedirectToDashboardGuard } from 'app/redirect-to-dashboard-guard.service';
import { FreightlancerCargoCategoryDetailPageGuard } from './freightlancer-cargo-category-detail-pages/freightlancer-cargo-category-detail-page-guard.service';
import { FreightlancerMembershipPageGuard } from './freightlancer-membership-page/freightlancer-membership-guard.service';
import { FreightlancerPageGuard } from './freightlancer-page-guard.service';
import { FreightlancerTransportRouteDetailsPageGuard } from './freightlancer-transport-routes-details-page/freightlancer-transport-routes-page-guard.service';

const routes: Routes = [
  {
    path: '',
    canActivate: [FreightlancerPageGuard],
    children: [
      {
        path: '',
        canActivate: [RedirectToDashboardGuard],
        loadChildren: () =>
          import(
            'app/freightlancer/freightlancer-landing-page/freightlancer-landing-page.module'
          ).then(m => m.FreightlancerLandingPageModule),
      },
      {
        path: 'new-client',
        redirectTo: '/post-load',
      },
      {
        path: 'how-it-works',
        loadChildren: () =>
          import(
            'app/freightlancer/freightlancer-how-it-works-pages/freightlancer-how-it-works-page.module'
          ).then(m => m.FreightlancerHowItWorksPageModule),
      },
      {
        path: 'membership',
        canActivate: [FreightlancerMembershipPageGuard],
        loadChildren: () =>
          import(
            'app/freightlancer/freightlancer-membership-page/freightlancer-membership-page.module'
          ).then(m => m.FreightlancerMembershipPageModule),
      },
      {
        path: 'faqs',
        loadChildren: () =>
          import(
            'app/freightlancer/freightlancer-faqs-page/freightlancer-faqs-page.module'
          ).then(m => m.FreightlancerFaqsPageModule),
      },
      {
        path: 'cargo-categories',
        children: [
          {
            path: '',
            loadChildren: () =>
              import(
                'app/freightlancer/freightlancer-cargo-categories-page/freightlancer-cargo-categories-page.module'
              ).then(m => m.FreightlancerCargoCategoriesPageModule),
          },
          {
            path: ':category',
            canActivate: [FreightlancerCargoCategoryDetailPageGuard],
            loadChildren: () =>
              import(
                'app/freightlancer/freightlancer-cargo-category-detail-pages/freightlancer-cargo-category-detail-pages.module'
              ).then(m => m.FreightlancerCargoCategoryDetailPagesModule),
          },
        ],
      },
      {
        path: 'verifications',
        data: {
          disableFooter: true,
        },
        children: [
          {
            path: '',
            loadChildren: () =>
              import(
                'app/freightlancer/verifications/verifications.module'
              ).then(m => m.VerificationsModule),
          },
        ],
      },
      {
        path: 'transport-routes',
        children: [
          {
            path: '',
            loadChildren: () =>
              import(
                'app/freightlancer/freightlancer-transport-routes-page/freightlancer-transport-routes-page.module'
              ).then(m => m.FreightlancerTransportRoutesPageModule),
          },
          {
            path: ':route',
            canActivate: [FreightlancerTransportRouteDetailsPageGuard],
            loadChildren: () =>
              import(
                'app/freightlancer/freightlancer-transport-routes-details-page/freightlancer-transport-routes-details-page.module'
              ).then(m => m.FreightlancerTransportRoutesDetailsPageModule),
          },
        ],
      },
      {
        path: 'support',
        children: [
          {
            path: '**',
            redirectTo: '/freightlancer/faqs',
          },
        ],
      },
      {
        path: 'post-project',
        redirectTo: '/post-load',
      },
      {
        path: 'projects',
        loadChildren: () =>
          import(
            'app/projects/project-view/freightlancer/load-view/freightlancer-load-view.module'
          ).then(m => m.FreightlancerLoadViewModule),
      },
      {
        path: 'signup',
        data: {
          disableFooter: true,
          adminOnlyMessaging: true,
          disableNavigation: true,
          disableToastNotifications: true,
          disableVerificationBanner: true,
          bypassLoggedOutRestricted: {
            native: true,
          },
        },
        loadChildren: () =>
          import(
            'app/login-signup/signup/freightlancer/freightlancer-signup-page.module'
          ).then(m => m.FreightlancerSignupPageModule),
      },
      {
        path: 'jobs',
        redirectTo: 'search/loads',
      },
      {
        path: 'search',
        data: {
          isMobilePrimaryView: true,
        },
        loadChildren: () =>
          import('app/search/freightlancer/freightlancer-search.module').then(
            m => m.FreightlancerSearchModule,
          ),
      },
      {
        path: 'terms-of-use',
        data: {
          bypassLoggedOutRestricted: {
            native: true,
          },
        },
        loadChildren: () =>
          import(
            'app/freightlancer/freightlancer-terms-of-use-page/freightlancer-terms-of-use-page.module'
          ).then(m => m.FreightlancerTermsOfUsePageModule),
      },
      {
        path: 'about',
        loadChildren: () =>
          import(
            'app/freightlancer/freightlancer-about-page/freightlancer-about-page.module'
          ).then(m => m.FreightlancerAboutPageModule),
      },
      {
        path: 'about/terms',
        redirectTo: 'terms-of-use',
      },
      {
        path: 'privacy-policy',
        data: {
          bypassLoggedOutRestricted: {
            native: true,
          },
        },
        loadChildren: () =>
          import(
            'app/freightlancer/freightlancer-privacy-policy-page/freightlancer-privacy-policy-page.module'
          ).then(m => m.FreightlancerPrivacyPolicyPageModule),
      },
      {
        path: 'about/privacy',
        redirectTo: 'privacy-policy',
      },
      {
        path: 'about',
        loadChildren: () =>
          import(
            'app/freightlancer/freightlancer-about-page/freightlancer-about-page.module'
          ).then(m => m.FreightlancerAboutPageModule),
      },
      {
        path: 'enterprise',
        loadChildren: () =>
          import(
            'app/freightlancer/freightlancer-enterprise-page/freightlancer-enterprise-page.module'
          ).then(m => m.FreightlancerEnterprisePageModule),
      },
      {
        path: 'tenders',
        loadChildren: () =>
          import(
            'app/freightlancer/loadshift-tender/loadshift-tender-landing.module'
          ).then(m => m.LoadshiftTenderLandingModule),
      },
      {
        path: 'login',
        data: {
          disableFooter: true,
          adminOnlyMessaging: true,
          disableNavigation: true,
          disableToastNotifications: true,
          disableVerificationBanner: true,
          bypassLoggedOutRestricted: {
            native: true,
          },
        },
        loadChildren: () =>
          import(
            'app/login-signup/login/freightlancer/freightlancer-login-page.module'
          ).then(m => m.FreightlancerLoginPageModule),
      },
      {
        path: 'internal/404',
        loadChildren: () =>
          import(
            'app/freightlancer/freightlancer-not-found-page/freightlancer-not-found-page.module'
          ).then(m => m.FreightlancerNotFoundPageModule),
      },
      {
        path: 'info/codeofconduct',
        loadChildren: () =>
          import(
            'app/freightlancer/freightlancer-code-of-conduct-page/freightlancer-code-of-conduct-page.module'
          ).then(m => m.FreightlancerCodeOfConductPageModule),
      },
      {
        path: 'thread-redirect',
        loadChildren: () =>
          import('app/thread-redirect-page/thread-redirect-page.module').then(
            m => m.ThreadRedirectPageModule,
          ),
      },
      {
        path: 'download',
        data: {
          disableMessaging: true,
          disableToastNotifications: true,
          disableVerificationBanner: true,
          bypassLoggedOutRestricted: {
            native: true,
          },
        },
        loadChildren: () =>
          import('app/download-app/download-app.module').then(
            m => m.DownloadAppModule,
          ),
      },
      {
        path: '**',
        canActivate: [NotFoundGuard],
        component: AppEmptyComponent,
      },
    ],
  },
];

@NgModule({
  imports: [RouterModule.forChild(routes)],
  providers: [],
  exports: [RouterModule],
})
export class FreightlancerRoutingModule {}
